import { logger } from './logger';

/**
 * Centralized authentication utilities
 * These functions can be used by any module that needs to handle auth operations
 */

/**
 * Clear all authentication data from localStorage
 * This is the core logout logic that should be consistent across the app
 */
export const clearAuthData = () => {
  try {
    logger.info('Clearing all authentication data');
    
    // Clear all auth-related localStorage items
    localStorage.removeItem('token');
    localStorage.removeItem('user_info');
    localStorage.removeItem('username');
    
    // Clear user context in logger
    logger.clearUser();
    
    // Notify other tabs/components about auth state change
    window.dispatchEvent(new Event('storage'));
    window.dispatchEvent(new Event('authChange'));
    
    logger.info('Authentication data cleared successfully');
    return true;
  } catch (error) {
    logger.error('Failed to clear authentication data', error);
    return false;
  }
};

/**
 * Force logout without API call - for invalid tokens or security issues
 * @param {string} reason - Reason for the forced logout
 */
export const forceLogout = (reason = 'Session expired') => {
  logger.info('Force logout triggered', { reason });
  clearAuthData();
};

/**
 * Validate auth state consistency (read-only)
 * Checks if token and user_info are both present or both absent
 * Does NOT make any changes to localStorage
 */
export const validateAuthState = () => {
  const token = localStorage.getItem('token');
  const userInfo = localStorage.getItem('user_info');
  
  // Check for inconsistent states but don't modify anything
  if (token && !userInfo) {
    logger.warn('Inconsistent auth state detected: token without user_info');
    return false;
  }
  
  if (userInfo && !token) {
    logger.warn('Inconsistent auth state detected: user_info without token');
    return false;
  }
  
  // Return true only if both exist or both are absent
  return !!token && !!userInfo;
};

/**
 * Debug authentication cache state
 * Useful for troubleshooting auth issues
 */
export const debugAuthCache = () => {
  const token = localStorage.getItem('token');
  const userInfo = localStorage.getItem('user_info');
  const username = localStorage.getItem('username');
  
  logger.debug('Auth cache state', {
    hasToken: !!token,
    tokenLength: token ? token.length : 0,
    hasUserInfo: !!userInfo,
    userInfoLength: userInfo ? userInfo.length : 0,
    hasUsername: !!username,
    username: username,
    isValid: validateAuthState()
  });
};